import { PermissionEnum } from '@api/enum';
import { IAddUserForm, IExternalLinkModel, IUser } from '@api/models';
import useAuthPermission from '@hooks/use-auth-permission';
import { Divider, Drawer } from '@hyperflake/react-ui-library';
import { FormHelperText, Label } from '@library/forms';
import { TRootState } from '@store/index';
import { ErrorMessage, Form, Formik, FormikHelpers } from 'formik';
import produce from 'immer';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import UserSelectView from '../UserSelectView/UserSelectView';
import { LoadingButton } from '@library/buttons';
import DocumentUserRow from '../DocumentUserRow/DocumentUserRow';
import * as Yup from 'yup';
import useAddUserToExternalLinkForm from 'modules/documents/hooks/useAddUserToExternalLinkForm';
import useRemoveUserFromExternalLinkForm from 'modules/documents/hooks/useRemoveUserFromExternalLinkForm';

export const ExternalLinkAccessSchema = Yup.object().shape({
    users: Yup.array().min(1, 'Please select at least one user.'),
});

interface ExternalLinkAccessDrawerProps {
    clientId: string;
    show: boolean;
    onClose: () => void;
    externalLink: IExternalLinkModel;
    selectedFolderId: string;
    onAccessUpdate: (externalLinkId: string, updatedExternalLink: IExternalLinkModel) => void;
    users: IUser[];
}

const ExternalLinkAccessDrawer = (props: ExternalLinkAccessDrawerProps) => {
    const { show, onClose, externalLink, selectedFolderId, onAccessUpdate, users, clientId } = props;

    const { hasPermission } = useAuthPermission();

    // const { user } = useAuthSelector();
    const { info: user } = useSelector((state: TRootState) => state.profile);

    const addUserToExternalLinkForm = useAddUserToExternalLinkForm({
        clientId,
        externalLink,
        folderId: selectedFolderId || undefined,
    });

    const removeUserFromExternalLinkForm = useRemoveUserFromExternalLinkForm({ clientId, externalLink });

    const handleAddUsersToExternal = useCallback(
        async (values: IAddUserForm, formikHelpers: FormikHelpers<IAddUserForm>) => {
            try {
                await addUserToExternalLinkForm.submit(values.users);

                // const newUsers = users.filter((user) => values.users.includes(user._id));
                const newUsers = users.filter((user) => values.users.includes(user._id)).map((row) => row);

                const updatedDocument = produce(externalLink, (draftState) => {
                    draftState.users = [...(externalLink.users as IUser[]), ...newUsers];

                    return draftState;
                });

                onAccessUpdate(externalLink._id, updatedDocument);

                toast.success('User added successfully.');

                formikHelpers.resetForm();
            } catch (err: any) {
                console.log(err);

                toast.error(err.data.message || 'Some error occurred, please try again.');
            }
        },
        [addUserToExternalLinkForm, externalLink, onAccessUpdate, users]
    );

    const handleRemoveUserFromExternalLink = useCallback(
        async (userId: string) => {
            try {
                await removeUserFromExternalLinkForm.submit(userId);

                const users = (externalLink.users as IUser[]).filter((u) => u._id !== userId);

                const updatedDocument = produce(externalLink, (draftState) => {
                    draftState.users = users;

                    return draftState;
                });

                onAccessUpdate(externalLink._id, updatedDocument);

                toast.success('User removed successfully.');
            } catch (err: any) {
                console.log(err);

                toast.error(err.data.message || 'Some error occurred, please try again.');
            }
        },
        [externalLink, onAccessUpdate, removeUserFromExternalLinkForm]
    );

    const externalLinkUsers = externalLink ? (externalLink.users as IUser[]) : [];

    const notAddedUers = users.filter((u) => {
        return externalLinkUsers.every((fu) => fu._id !== u._id);
    });

    return (
        <Drawer open={show} onClose={onClose}>
            <div className="flex flex-col min-h-[calc(100dvh-3rem)]">
                <h4>{externalLink?.name} Access</h4>

                {(hasPermission(PermissionEnum.DOCUMENT_UPDATE) ||
                    (externalLink?.uploadedBy as IUser)?._id === user._id) &&
                    notAddedUers.length > 0 && (
                        <>
                            <div className="mt-6">
                                <Formik
                                    initialValues={{
                                        users: [],
                                    }}
                                    validationSchema={ExternalLinkAccessSchema}
                                    enableReinitialize
                                    onSubmit={handleAddUsersToExternal}
                                >
                                    {({ values, setFieldValue }) => (
                                        <Form>
                                            <div>
                                                <Label htmlFor="email">Users</Label>

                                                <UserSelectView
                                                    users={notAddedUers}
                                                    selectedUsers={values.users}
                                                    onSelectedUsersChange={(values) => setFieldValue('users', values)}
                                                />

                                                {/* <ErrorMessage
                                                    component="div"
                                                    className="form-error-message"
                                                    name="users"
                                                /> */}
                                                <FormHelperText variant={'error'}>
                                                    <ErrorMessage name="users" />
                                                </FormHelperText>
                                            </div>

                                            <div className="mt-6">
                                                <LoadingButton
                                                    type="submit"
                                                    size="sm"
                                                    isLoading={addUserToExternalLinkForm.isLoading}
                                                    loadingText="Adding"
                                                >
                                                    Add
                                                </LoadingButton>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>

                            <Divider className="mt-7 mb-6" />
                        </>
                    )}

                <div className="text-grayscale-500 font-medium mt-6">Current members</div>

                {externalLinkUsers.map((row) => (
                    <DocumentUserRow
                        uploadedBy={externalLink.uploadedBy}
                        key={row._id}
                        user={row}
                        onAccessRevoke={() => handleRemoveUserFromExternalLink(row._id)}
                        isLoading={removeUserFromExternalLinkForm.isLoading}
                    />
                ))}
            </div>
        </Drawer>
    );
};

export default ExternalLinkAccessDrawer;
